import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getInvoice, getInvoiceBilling } from '../../apis/backend';
import { Invoice } from '../../models/Invoice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import {
    buildInvoiceOptionsByYearFromFirstBilling,
    computeLatestAvailableInvoicePeriod,
    DateAndMonth
} from './invoicePeriod';
import { isObjectNotEmpty } from '../../utils/tools';
import { DISHOP, ERROR_GET_INVOICE, NETWORK } from '../../constants';
import * as actions from '../../actions/index';
import { useKeepTrue } from '../../utils/custom_hooks/useKeepTrue';
import { hasAnyLegalInfo } from '../../utils/LegalUtils';

const NO_INVOICE_PERIOD_LABEL = 'Aucune facture disponible';

export type UseInvoiceParams = {
    companyId?: string | null;
    shopId?: string | null;
    isNetwork?: boolean;
    dateAndMonth: DateAndMonth;
    enabled?: boolean;
};

export type UseInvoiceViewModel = {
    invoice: Invoice | null;
    isLoading: boolean;
    loadingInvoice: boolean;
    error: unknown;
    currentYear: number;
    periodeLabel: string;
    dateAndMonth: DateAndMonth;
    optionFinance: Record<number, DateAndMonth[]>;
    safeOptionFinance: Record<number, DateAndMonth[]>;
    anchorEl: HTMLElement | null;
    anchorElYear: HTMLElement | null;
    hasPeriods: boolean;
    isInvoiceReady: boolean;
    handleHelp: () => void;
    handleClick: (e: React.MouseEvent<HTMLElement>) => void;
    handleClickYear: (e: React.MouseEvent<HTMLElement>) => void;
    handleClose: () => void;
    handleFetchData: (p: DateAndMonth) => void;
    handleUpdateYear: (newYear: number) => void;
    shouldRenderPdf: boolean;
    isNetwork: boolean;
};

export type UseInvoiceResult<TInvoice = Invoice> = {
    invoice: TInvoice | null;
    loadingInvoice: boolean;
    error: unknown;
    refetch: () => Promise<void>;
};

const useInvoice = <TInvoice = Invoice>({

                                            companyId,
                                            shopId,
                                            isNetwork,
                                            dateAndMonth,
                                            enabled
                                        }: UseInvoiceParams): UseInvoiceResult<TInvoice> => {
    const [invoice, setInvoice] = useState<TInvoice | null>(null);
    const [loadingInvoice, setLoadingInvoice] = useState(false);
    const [error, setError] = useState<unknown>(null);
    const requestIdRef = useRef(0);
    const fetchInvoice = useCallback(async () => {
        const reqId = ++requestIdRef.current;
        setLoadingInvoice(true);
        setError(null);
        try {
            const effectiveShopId: string | null = isNetwork ? null : (shopId ?? null);
            const payload = {
                monthNumber: dateAndMonth.monthNumber,
                year: dateAndMonth.year,
                firstHalf: dateAndMonth.firstHalf,
            };
            const data = await getInvoice(companyId ?? null, effectiveShopId, payload);
            if (reqId !== requestIdRef.current) {
                {
                    return;
                }
            }
            setInvoice((data as TInvoice) ?? null);
        } catch (e) {
            if (reqId !== requestIdRef.current) {
                {
                    return;
                }
            }
            setError(e);
            setInvoice(null);
        } finally {
            if (reqId === requestIdRef.current) {
                setLoadingInvoice(false);
            }
        }
    }, [companyId, shopId, isNetwork, dateAndMonth]);

    useEffect(() => {
        if (!enabled || !companyId || (!isNetwork && !shopId)) {
            return;
        }
        fetchInvoice();
    }, [enabled, companyId, shopId, isNetwork, fetchInvoice]);

    return { invoice, loadingInvoice, error, refetch: fetchInvoice };
};

export const useInvoiceViewModel = (): UseInvoiceViewModel => {
    const dispatch = useAppDispatch();
    const { shopId, isAllShops, companyId } = useAppSelector((state) => state.user);

    const initialLatest = useMemo<DateAndMonth>(() => computeLatestAvailableInvoicePeriod(), []);

    const [isLoading, setIsLoading] = useState(true);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [anchorElYear, setAnchorElYear] = useState<HTMLElement | null>(null);

    const [currentYear, setCurrentYear] = useState<number>(initialLatest.year);
    const [periodeLabel, setPeriodeLabel] = useState<string>(initialLatest.toShow ?? 'En recherche ...');
    const [dateAndMonth, setDateAndMonth] = useState<DateAndMonth>(initialLatest);

    const [optionFinance, setOptionFinance] = useState<Record<number, DateAndMonth[]>>({});
    const [hasSelectedPeriod, setHasSelectedPeriod] = useState(true);

    const isInvoiceReady = Boolean(companyId && (isAllShops || shopId) && hasSelectedPeriod);
    const isNetwork = Boolean(isAllShops);

    const { invoice, loadingInvoice, error } = useInvoice({
        companyId, shopId, isNetwork, dateAndMonth,
        enabled: isInvoiceReady,
    });

    const hasLegalMinimum = (invoice: Invoice | null) => {
        if (!invoice) {
            return false;
        }
        if (invoice?.serviceBillings?.[DISHOP] && !hasAnyLegalInfo(invoice?.dishopLegal, {
            showEmail: false,
            showRcs: true
        })) {
            return false
        }
        if (invoice?.serviceBillings?.[NETWORK] && !hasAnyLegalInfo(invoice?.networkLegal, {
            showEmail: false,
            showRcs: true
        })) {
            return false
        }
        return !(!Boolean(isAllShops) && !hasAnyLegalInfo(invoice?.shopLegal, { showEmail: false, showRcs: true }));

    };
    const canRenderPdfRaw =
        isInvoiceReady &&
        !loadingInvoice &&
        isObjectNotEmpty(invoice?.serviceBillings) &&
        hasLegalMinimum(invoice);

    const safeOptionFinance = useMemo(
        () => ({
            ...optionFinance,
            [currentYear]: optionFinance[currentYear] ?? [],
        }),
        [optionFinance, currentYear]
    );

    const hasPeriods = useMemo(() => isObjectNotEmpty(optionFinance), [optionFinance]);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                setIsLoading(true);
                const latest = computeLatestAvailableInvoicePeriod();
                if (!mounted) {
                    return;
                }
                setDateAndMonth(latest);
                setCurrentYear(latest.year);
                const firstBilling = await getInvoiceBilling(true);
                const firstDate = (Object.values(firstBilling ?? {})?.[0] as { date: string })?.date;
                if (!mounted) {
                    return;
                }
                if (!firstDate) {
                    setOptionFinance({});
                    setPeriodeLabel(NO_INVOICE_PERIOD_LABEL);
                    setHasSelectedPeriod(false);
                    setIsLoading(false);
                    return;
                }

                const opts = buildInvoiceOptionsByYearFromFirstBilling(latest, firstDate);
                setOptionFinance(opts);

                setPeriodeLabel(latest.toShow ?? 'Selectionnez une période');
                setHasSelectedPeriod(true);

                setIsLoading(false);
            } catch {
                if (!mounted) {
                    return;
                }
                setIsLoading(false);
                dispatch(actions.openErrorModal(ERROR_GET_INVOICE));
            }
        })();

        return () => {
            mounted = false;
        };
    }, [dispatch]);

    useEffect(() => {
        if (!error) {
            return;
        }
        dispatch(actions.openErrorModal(ERROR_GET_INVOICE));
    }, [error, dispatch]);

    const handleHelp = useCallback(() => {
        dispatch(actions.showHelpModal('factures'));
    }, [dispatch]);

    const handleClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(e.currentTarget);
    }, []);

    const handleClickYear = useCallback((e: React.MouseEvent<HTMLElement>) => {
        setAnchorElYear(e.currentTarget);
    }, []);

    const handleClose = useCallback(() => {
        setAnchorEl(null);
        setAnchorElYear(null);
    }, []);

    const handleFetchData = useCallback((period: DateAndMonth) => {
        setAnchorEl(null);
        setPeriodeLabel(period.toShow ?? 'Selectionnez une période');
        setDateAndMonth(period);
        setHasSelectedPeriod(true);
    }, []);

    const handleUpdateYear = useCallback((newYear: number) => {
        setCurrentYear(newYear);
        setAnchorElYear(null);
        setPeriodeLabel('Selectionnez une période');
        setHasSelectedPeriod(false);
    }, []);

    const shouldRenderPdf = useKeepTrue(canRenderPdfRaw, 100);

    return {
        invoice,
        isLoading,
        loadingInvoice,
        error,
        currentYear,
        periodeLabel,
        dateAndMonth,
        optionFinance,
        safeOptionFinance,
        anchorEl,
        anchorElYear,
        hasPeriods,
        isInvoiceReady,
        handleHelp,
        handleClick,
        handleClickYear,
        handleClose,
        handleFetchData,
        handleUpdateYear,
        shouldRenderPdf,
        isNetwork
    };
};